import React, { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLang } from "@/lib/i18n-context";
import { FORM_CONFIGS } from "@/lib/form-configs";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, ArrowLeft, Loader2, ShieldCheck, Check } from "lucide-react";

function Field({ f, value, onChange }) {
  const common = { id: f.name, "data-testid": `field-${f.name}` };
  if (f.type === "text" || f.type === "phone" || f.type === "email") {
    return (
      <Input
        {...common}
        type={f.type === "email" ? "email" : "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={f.maxLength}
      />
    );
  }
  if (f.type === "number") {
    return (
      <Input
        {...common}
        type="number"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (f.type === "textarea") {
    return (
      <Textarea
        {...common}
        rows={4}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={f.maxLength}
      />
    );
  }
  if (f.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`field-${f.name}`}>
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {f.options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`field-${f.name}-opt-${o}`}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === "checkbox") {
    return (
      <div className="flex items-start gap-3">
        <Checkbox
          id={f.name}
          data-testid={`field-${f.name}`}
          checked={!!value}
          onCheckedChange={onChange}
          className="mt-0.5"
        />
        <label htmlFor={f.name} className="text-sm leading-relaxed cursor-pointer">
          {f.label}
        </label>
      </div>
    );
  }
  return null;
}

export default function RegisterFormPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const cfg = FORM_CONFIGS[category];
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState({});
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = useMemo(() => (cfg ? cfg.steps.length + 1 : 0), [cfg]); // +1 for T&C step

  if (!cfg) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Unknown registration type</h1>
        <Link to="/register" className="text-brand-red underline mt-4 inline-block">
          Back to registration hub
        </Link>
      </div>
    );
  }

  const setField = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const validateStep = () => {
    if (stepIdx >= cfg.steps.length) return true;
    const step = cfg.steps[stepIdx];
    for (const f of step.fields) {
      if (f.required && (values[f.name] === undefined || values[f.name] === "" || values[f.name] === false)) {
        toast.error(`"${f.label}" is required.`);
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStepIdx((s) => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStepIdx((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!terms) {
      toast.error(t("terms_agree"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/registrations", {
        category,
        data: values,
        accepted_terms: true,
      });
      toast.success("Registration submitted!");
      navigate("/dashboard", { state: { newRegId: data.id } });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const isTermsStep = stepIdx === cfg.steps.length;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-16">
      <div className="mb-6">
        <Link to="/register" className="text-xs uppercase tracking-widest text-brand-red font-semibold hover:underline" data-testid="back-to-hub">
          ← Back to registration hub
        </Link>
      </div>

      <h1 className="font-display text-3xl md:text-4xl font-bold" data-testid="form-title">
        {cfg.title[lang] || cfg.title.en}
      </h1>
      <p className="text-muted-foreground mt-3">{cfg.intro[lang] || cfg.intro.en}</p>

      <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="w-4 h-4 text-brand-red" />
        Signed in as <span className="font-semibold text-foreground">{user?.email}</span> · {t("privacy_note")}
      </div>

      {/* Progress */}
      <div className="mt-8 flex items-center gap-2" data-testid="form-progress">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i <= stepIdx ? "bg-brand-red" : "bg-border"
            }`}
          />
        ))}
      </div>
      <div className="mt-3 text-xs text-muted-foreground">
        Step {stepIdx + 1} of {totalSteps}
      </div>

      <div className="mt-8 bg-white border border-border rounded-2xl p-6 md:p-8 shadow-sm">
        {!isTermsStep ? (
          <>
            <h3 className="font-display text-xl font-bold mb-6">{cfg.steps[stepIdx].title}</h3>
            <div className="space-y-5">
              {cfg.steps[stepIdx].fields.map((f) => (
                <div key={f.name}>
                  {f.type !== "checkbox" && (
                    <Label htmlFor={f.name} className="mb-1.5 block text-sm font-medium">
                      {f.label} {f.required && <span className="text-brand-red">*</span>}
                    </Label>
                  )}
                  <Field
                    f={f}
                    value={values[f.name]}
                    onChange={(v) => setField(f.name, v)}
                  />
                  {f.help && (
                    <div className="text-xs text-muted-foreground mt-1.5">{f.help}</div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <h3 className="font-display text-xl font-bold mb-4">Terms & Conditions</h3>
            <div className="bg-brand-canvas rounded-xl p-5 max-h-80 overflow-y-auto text-sm leading-relaxed space-y-3">
              <p>
                By registering as a participant of the <strong>Indonesia-Japan Friendship Festival 2026 (IJFF 2026)</strong>,
                you agree to the following terms:
              </p>
              <ol className="list-decimal list-inside space-y-2">
                <li><strong>Booking commitment.</strong> Registration is a commitment to participate on <strong>12 – 13 December 2026</strong> at Shiokaze Park, Odaiba, Tokyo.</li>
                <li><strong>Deposit.</strong> Where a deposit is required (Performers and booth participants), the deposit will be requested by the organizer after registration is approved. The deposit is <strong>refundable within 14 days after the event</strong>, subject to confirmed participation and compliance with the organizer&apos;s rulebook.</li>
                <li><strong>Cancellation.</strong> Cancellation less than 30 days before the event forfeits 100% of the deposit. Cancellation earlier will be reviewed case-by-case.</li>
                <li><strong>Food safety (F&B booths).</strong> Food and catering participants must comply with the Japanese Food Sanitation Law (食品衛生法) and hold or acquire a Food Sanitation Manager (食品衛生責任者) certification. The organizer may inspect and refuse operation of any non-compliant booth without refund.</li>
                <li><strong>Facilities.</strong> Standard booth equipment is 2 tables + 4 chairs unless otherwise specified. Additional facilities (gas, water, refrigerator, sink) will be provided only for participants who declared them at registration.</li>
                <li><strong>Content.</strong> Performances and displayed content must be respectful of Indonesian and Japanese cultural sensitivities. The organizer reserves the right to modify the rundown for operational reasons.</li>{"\u200B"}
                <li><strong>Data & privacy.</strong> Personal and business data submitted through this website is confidential. Only the organizer team and the registrar themselves can view submitted data. It will not be shared with third parties without written consent.</li>
                <li><strong>Media consent.</strong> Photos and video captured during the event may be used for IJFF marketing and post-event reporting.</li>
                <li><strong>Force majeure.</strong> In case of natural disaster, epidemic or government restriction preventing the event, deposits will be fully refunded.</li>
                <li><strong>Governing law.</strong> Any dispute will be governed by the laws of Japan and settled in Tokyo District Court.</li>
              </ol>
            </div>
            <div className="mt-6 flex items-start gap-3">
              <Checkbox
                id="agree_terms"
                checked={terms}
                onCheckedChange={setTerms}
                data-testid="terms-checkbox"
                className="mt-0.5"
              />
              <label htmlFor="agree_terms" className="text-sm leading-relaxed cursor-pointer">
                {t("terms_agree")}
              </label>
            </div>
          </>
        )}

        <div className="mt-8 flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={back}
            disabled={stepIdx === 0}
            className="rounded-full"
            data-testid="form-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> {t("back")}
          </Button>
          {!isTermsStep ? (
            <Button
              onClick={next}
              className="rounded-full bg-brand-red hover:bg-brand-red-dark text-white"
              data-testid="form-next-btn"
            >
              {t("next")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={submitting || !terms}
              className="rounded-full bg-brand-red hover:bg-brand-red-dark text-white"
              data-testid="form-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" /> {t("submit")}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
